import { searchContext } from '../../../../context/Context';
import { renderMappingButton } from '../renders/buttonRenderers';
import dropdownLists from '../../DropdownLists';
import { variantLink } from '../renders/MappingFunctions';
import {
  renderAlleleDataVDA,
  renderAlleleDataVariant,
  renderRoundedValues,
} from '../renders/CommonAtributes';
import { getHeaderProps } from '../config/helpers';
import { createSelectFilter } from '../filters/filterConfigs';
import { ColumnDefinition } from '../shared/column-types';
import { renderNumericColumn, renderTextColumn } from '@/utils/renderTableValue';

export const getVariantColumns = (context: any): ColumnDefinition[] => {
  const { tab } = context;
  const headerProps = getHeaderProps();

  const columns = [
    {
      name: 'exome',
      category: 'Variant',
      sortable: true,
      header: <span title="Allele Frequency in GNOMAD Exomes">AF EXOME</span>,
      uniqueID: 'AFExome',
      headerProps,
      render: row => {
        const { tab } = searchContext();
        if (tab === 'VARIANTS') {
          return renderAlleleDataVariant(row, 'exome');
        } else if (tab === 'VDA') {
          return renderAlleleDataVDA(row, 'exome');
        }
      },
    },

    {
      name: 'alleleFreqExome',
      category: 'Variant',
      sortable: true,
      header: <span title="Allele Frequency in GNOMAD Exomes">AF EXOME</span>,
      headerProps,
      render: row => {
        const { tab } = searchContext();
        if (tab === 'VARIANTS') {
          return renderAlleleDataVariant(row, 'alleleFreqExome');
        } else if (tab === 'VDA' || tab === 'DDA') {
          return renderAlleleDataVDA(row, 'alleleFreqExome');
        }
      },
      minWidth: 115,
    },

    {
      name: 'genome',
      category: 'Variant',
      sortable: true,
      header: <span title="Allele Frequency in GNOMAD Genomes">AF GENOME</span>,
      uniqueID: 'AFGenome',
      headerProps,
      render: instance => renderAlleleDataVariant(instance, 'genome'),
    },

    {
      name: 'alleleFreqGenome',
      category: 'Variant',
      sortable: true,
      header: <span title="Allele Frequency in GNOMAD Genomes">AF GENOME</span>,
      headerProps,
      render: row => {
        const { tab } = searchContext();
        if (tab === 'VARIANTS') {
          return renderAlleleDataVariant(row, 'alleleFreqGenome');
        } else if (tab === 'VDA' || tab === 'DDA') {
          return renderAlleleDataVDA(row, 'alleleFreqGenome');
        }
      },
      minWidth: 115,
    },

    {
      name: 'alleles',
      category: 'Variant',
      sortable: true,
      header: <span title="Reference/Alternative Allele">Alleles</span>,
      headerProps,
      minWidth: 115,
      render: ({ data }) => {
        const { tab } = searchContext();
        let alleles = '';

        if (tab === 'VARIANTS') {
          const allelesArray = data.alleles?.map(value => `${value.ref}\\${value.alt}`);
          alleles = allelesArray ? allelesArray.join('; ') : '';
        } else if (tab === 'VDA' || tab === 'DDA') {
          const ref = data.alleles_ref;
          const alt = data.alleles_alt;
          alleles = `${ref}/${alt}`;
        }

        return <span>{alleles}</span>;
      },
    },

    {
      name: 'chromosome',
      category: 'Variant',
      sortable: true,
      header: <span title="Chromosome">Chr</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderNumericColumn(value),
    },
    {
      name: 'hgvsc',
      category: 'Variant',
      sortable: true,
      header: (
        <span title="Coding DNA reference sequence to describe variants (based on a protein coding transcript)">
          Hgvs<sub>c</sub>
        </span>
      ),
      headerProps,
      minWidth: 220,
      render: ({ value }) => {
        const hgvscValue = Array.isArray(value) ? value[0] : value;
        return renderTextColumn(hgvscValue);
      },
    },
    {
      name: 'hgvsp',
      category: 'Variant',
      sortable: true,
      header: (
        <span title="Protein reference sequence to describe variants">
          Hgvs<sub>p</sub>
        </span>
      ),
      headerProps,
      minWidth: 220,
      render: ({ value }) => {
        const hgvspValue = Array.isArray(value) ? value[0] : value;
        return renderTextColumn(hgvspValue);
      },
    },

    {
      name: 'coord',
      category: 'Variant',
      sortable: true,
      header: <span title="Position in the Chromosome">Position</span>,
      headerProps,
      minWidth: 150,
      render: ({ value }) => renderNumericColumn(value),
    },

    {
      name: 'DBSNPclass',
      category: 'Variant',
      sortable: true,
      header: <span title="Class of variant">Variant type</span>,
      minWidth: 150,
      uniqueID: 'DBSNPClass',
      headerProps,
      ...createSelectFilter(dropdownLists.DBSNPclass),
      render: ({ data }) => {
        const { tab } = searchContext();
        const value = tab === 'VARIANTS' ? data.dbnspClass : data.DBSNPclass;

        return renderTextColumn(value);
      },
    },

    {
      name: 'polyphen_score',
      category: 'Variant',
      sortable: true,
      header: <span title="PolyPhen">PolyPhen</span>,
      headerProps,
      render: instance => renderRoundedValues(instance, 'polyphen_score'),
      minWidth: 85,
    },

    {
      name: 'sift_score',
      category: 'Variant',
      sortable: true,
      header: <span title="SIFT">SIFT</span>,
      headerProps,
      render: instance => renderRoundedValues(instance, 'sift_score'),
      minWidth: 85,
    },

    {
      name: 'mostSevereConsequences',
      category: 'Variant',
      sortable: true,
      header: <span title="Most Severe VEP Consequence Type">Consequence</span>,
      headerProps,
      ...createSelectFilter(dropdownLists.mostSevereConsequences),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'strID',
      category: 'Variant',
      sortable: true,
      header: <span title="Variant">Variant</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const value = renderTextColumn(data.strID);
        if (value === '-') return '-';
        return variantLink(value);
      },
    },

    {
      name: 'variantStrID',
      category: 'Variant',
      sortable: true,
      header: <span title="Variant">Variant</span>,
      headerProps,
      render: ({ data }) => {
        const rawValue = data.variantStrID || data.strID;
        const value = renderTextColumn(rawValue);

        if (value === '-') return '-';

        return variantLink(value);
      },
    },

    {
      name: 'variantDPI',
      category: 'Variant',
      sortable: true,
      header: (
        <span title="Disease Pleiotropy Index for the variant">
          DPI<sub>v</sub>
        </span>
      ),
      headerProps,
      minWidth: 180,
    },

    {
      name: 'variantDSI',
      category: 'Variant',
      sortable: true,
      header: (
        <span title="Disease Specificity Index for the variant">
          DSI<sub>v</sub>
        </span>
      ),
      headerProps,
      minWidth: 200,
    },

    {
      name: 'numDiseasesAssociatedToVariant',
      category: 'Variant',
      sortable: true,
      header: (
        <span title="Number of diseases associated to the variant">
          N diseases<sub>v</sub>
        </span>
      ),
      headerProps,
      minWidth: 115,
      render: ({ data }) => {
        const { tab } = searchContext();
        let mappingItems: any[] = [];

        if (tab === 'VARIANTS') {
          mappingItems = [
            {
              filterParam: 'variant_name',
              filterValue: data.strID,
              name: data.strID,
              id: data.strID,
            },
          ];
        } else if (tab === 'VDA') {
          mappingItems = [
            {
              filterParam: 'variant',
              filterValue: data.variantStrID,
              name: data.variantStrID,
              id: data.variantStrID,
            },
          ];
        } else {
          return renderNumericColumn(data?.numDiseasesAssociatedToVariant);
        }

        if (!data.numDiseasesAssociatedToVariant)
          return renderNumericColumn(data?.numDiseasesAssociatedToVariant);

        return renderMappingButton(data, {
          displayValue: renderNumericColumn(data.numDiseasesAssociatedToVariant),
          mappingItems,
          columVisibilityModelKey: 'numDiseasesAssociatedToVariant',
          tabSegment: 'vda',
          name: 'numDiseasesAssociatedToVariant',
          viewSegment: '/summary',
        });
      },
    },

    {
      name: 'DPI',
      category: tab === 'VARIANTS' ? 'Variant' : 'Gene',
      sortable: true,
      header: (
        <span title="Disease Pleiotropy Index for the gene">
          DPI<sub>{tab === 'VARIANTS' ? 'v' : 'g'}</sub>
        </span>
      ),
      headerProps,
      render: instance => renderRoundedValues(instance, 'DPI'),
      minWidth: 85,
    },

    {
      name: 'DSI',
      category: tab === 'VARIANTS' ? 'Variant' : 'Gene',
      sortable: true,
      header: (
        <span title="Disease Specificity Index for the gene">
          DSI<sub>{tab === 'VARIANTS' ? 'v' : 'g'}</sub>
        </span>
      ),
      headerProps,
      render: instance => renderRoundedValues(instance, 'DSI'),
      minWidth: 85,
    },
  ];

  return columns;
};
